import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import './App.css';

const ARAgingReport = () => {
  const [reportData, setReportData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'detail'
  const toLocalDateString = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const [asOfDate, setAsOfDate] = useState(toLocalDateString(new Date()));

  const getEndOfLastMonth = () => {
    const d = new Date();
    return toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 0));
  };

  const getEndOfThisMonth = () => {
    const d = new Date();
    return toLocalDateString(new Date(d.getFullYear(), d.getMonth() + 1, 0));
  };

  const calculateAgingSummary = useCallback((invoices, referenceDate) => {
    const ref = new Date(referenceDate + 'T23:59:59');
    const customerMap = new Map();

    invoices.forEach(invoice => {
      if (!invoice.customers) return;

      const customerId = invoice.customer_id;
      const dueDate = new Date(invoice.due_date);
      const daysOverdue = Math.floor((ref - dueDate) / (1000 * 60 * 60 * 24));
      const amount = parseFloat(invoice.total_amount);

      if (!customerMap.has(customerId)) {
        customerMap.set(customerId, {
          customer_id: customerId,
          first_name: invoice.customers.first_name,
          last_name: invoice.customers.last_name,
          company: invoice.customers.company,
          phone: invoice.customers.phone,
          email: invoice.customers.email,
          invoice_count: 0,
          current_total: 0,
          days_31_60_total: 0,
          days_61_90_total: 0,
          days_90_plus_total: 0,
          total_outstanding: 0
        });
      }

      const customer = customerMap.get(customerId);
      customer.invoice_count++;
      customer.total_outstanding += amount;

      if (daysOverdue <= 0) {
        customer.current_total += amount;
      } else if (daysOverdue <= 30) {
        customer.days_31_60_total += amount;
      } else if (daysOverdue <= 60) {
        customer.days_61_90_total += amount;
      } else {
        customer.days_90_plus_total += amount;
      }
    });

    return Array.from(customerMap.values())
      .filter(c => c.total_outstanding > 0)
      .sort((a, b) => b.total_outstanding - a.total_outstanding);
  }, []);

  const loadARAgingData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          invoice_date,
          due_date,
          total_amount,
          status,
          customer_id,
          customers (
            id,
            first_name,
            last_name,
            company,
            phone,
            email
          )
        `)
        .in('status', ['Sent', 'Overdue'])
        .lte('invoice_date', asOfDate)
        .order('customer_id');

      if (invoicesError) throw invoicesError;

      const summary = calculateAgingSummary(invoicesData, asOfDate);
      setReportData(summary);
      setDetailData(invoicesData);
    } catch (err) {
      console.error('Error loading A/R aging data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [asOfDate, calculateAgingSummary]);

  useEffect(() => {
    loadARAgingData();
  }, [loadARAgingData]);

  const getCustomerInvoices = (customerId) => {
    return detailData.filter(inv => inv.customer_id === customerId);
  };

  const calculateDaysOverdue = (dueDate) => {
    const ref = new Date(asOfDate + 'T23:59:59');
    const due = new Date(dueDate);
    return Math.floor((ref - due) / (1000 * 60 * 60 * 24));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-CA');
  };

  const getAgingBucket = (dueDate) => {
    const daysOverdue = calculateDaysOverdue(dueDate);
    if (daysOverdue <= 0) return 'Current';
    if (daysOverdue <= 30) return '31-60';
    if (daysOverdue <= 60) return '61-90';
    return '90+';
  };

  const calculateTotals = () => {
    return reportData.reduce(
      (totals, row) => ({
        current: totals.current + (row.current_total || 0),
        days31_60: totals.days31_60 + (row.days_31_60_total || 0),
        days61_90: totals.days61_90 + (row.days_61_90_total || 0),
        days90Plus: totals.days90Plus + (row.days_90_plus_total || 0),
        total: totals.total + (row.total_outstanding || 0)
      }),
      { current: 0, days31_60: 0, days61_90: 0, days90Plus: 0, total: 0 }
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    const headers = ['Customer', 'Company', 'Phone', 'Current', '31-60 Days', '61-90 Days', '90+ Days', 'Total Outstanding'];
    const rows = reportData.map(row => [
      `${row.first_name} ${row.last_name}`,
      row.company || '',
      row.phone,
      row.current_total?.toFixed(2) || '0.00',
      row.days_31_60_total?.toFixed(2) || '0.00',
      row.days_61_90_total?.toFixed(2) || '0.00',
      row.days_90_plus_total?.toFixed(2) || '0.00',
      row.total_outstanding?.toFixed(2) || '0.00'
    ]);

    const totals = calculateTotals();
    rows.push([
      'TOTAL', '', '',
      totals.current.toFixed(2),
      totals.days31_60.toFixed(2),
      totals.days61_90.toFixed(2),
      totals.days90Plus.toFixed(2),
      totals.total.toFixed(2)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ar_aging_report_${asOfDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading A/R Aging Report...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <strong>Error loading report:</strong> {error}
        </div>
        <button onClick={loadARAgingData} style={{ padding: '8px 16px' }}>
          Retry
        </button>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', backgroundColor: '#fff' }}>
      {/* Print Header - Only shows when printing */}
      <div className="print-only" style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 10px 0', color: '#333' }}>
          JFT Works
        </h1>
        <h2 style={{ fontSize: '18px', fontWeight: 'normal', margin: '0 0 5px 0', color: '#666' }}>
          A/R AGING SUMMARY
        </h2>
        <p style={{ fontSize: '14px', margin: '0', color: '#666' }}>
          As of {formatDate(asOfDate)}
        </p>
      </div>

      {/* Screen Controls - Hidden when printing */}
      <div className="no-print" style={{ marginBottom: '20px' }}>
        <h1>Accounts Receivable Aging Report</h1>

        {/* As-of Date Selector */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          marginBottom: '16px',
          padding: '12px 16px',
          backgroundColor: '#f5f7fa',
          borderRadius: '6px',
          border: '1px solid #e0e4ea'
        }}>
          <strong style={{ whiteSpace: 'nowrap' }}>As of:</strong>

          {/* Month dropdown */}
          <select
            value={parseInt(asOfDate.split('-')[1]) - 1}
            onChange={e => {
              const [y, , d] = asOfDate.split('-');
              const newMonth = parseInt(e.target.value);
              const lastDay = new Date(parseInt(y), newMonth + 1, 0).getDate();
              const clampedDay = Math.min(parseInt(d), lastDay);
              setAsOfDate(toLocalDateString(new Date(parseInt(y), newMonth, clampedDay)));
            }}
            style={{ padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }}
          >
            {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
              <option key={i} value={i}>{m}</option>
            ))}
          </select>

          {/* Year dropdown */}
          <select
            value={asOfDate.split('-')[0]}
            onChange={e => {
              const [, m, d] = asOfDate.split('-');
              const newYear = parseInt(e.target.value);
              const lastDay = new Date(newYear, parseInt(m), 0).getDate();
              const clampedDay = Math.min(parseInt(d), lastDay);
              setAsOfDate(toLocalDateString(new Date(newYear, parseInt(m) - 1, clampedDay)));
            }}
            style={{ padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }}
          >
            {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 4 + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {/* Day input */}
          <input
            type="number"
            min="1"
            max="31"
            value={parseInt(asOfDate.split('-')[2])}
            onChange={e => {
              const [y, m] = asOfDate.split('-');
              const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
              const day = Math.min(Math.max(1, parseInt(e.target.value) || 1), lastDay);
              setAsOfDate(toLocalDateString(new Date(parseInt(y), parseInt(m) - 1, day)));
            }}
            style={{ padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', width: '60px' }}
          />

          <div style={{ width: '1px', height: '24px', backgroundColor: '#ccc', margin: '0 4px' }} />

          <button
            onClick={() => setAsOfDate(toLocalDateString(new Date()))}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer', backgroundColor: '#fff' }}
          >
            Today
          </button>
          <button
            onClick={() => setAsOfDate(getEndOfLastMonth())}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer', backgroundColor: '#fff' }}
          >
            End of Last Month
          </button>
          <button
            onClick={() => setAsOfDate(getEndOfThisMonth())}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer', backgroundColor: '#fff' }}
          >
            End of This Month
          </button>
          <span style={{ marginLeft: 'auto', fontWeight: 'bold' }}>
            Total Outstanding: {formatCurrency(totals.total)}
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '20px' }}>
          <button
            onClick={() => setViewMode(viewMode === 'summary' ? 'detail' : 'summary')}
            style={{ padding: '8px 16px' }}
          >
            {viewMode === 'summary' ? 'Show Detail' : 'Show Summary'}
          </button>
          <button onClick={handleExport} style={{ padding: '8px 16px' }}>
            Export CSV
          </button>
          <button onClick={handlePrint} style={{ padding: '8px 16px' }}>
            Print
          </button>
          <button onClick={loadARAgingData} style={{ padding: '8px 16px' }}>
            Refresh
          </button>
        </div>
      </div>

      {reportData.length === 0 ? (
        <div style={{
          backgroundColor: '#e8f5e9',
          border: '1px solid #4caf50',
          borderRadius: '4px',
          padding: '20px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '16px' }}>
            No outstanding invoices found as of {formatDate(asOfDate)}. All accounts are current!
          </p>
        </div>
      ) : (
        <>
          {viewMode === 'summary' ? (
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '12px',
              border: '1px solid #000'
            }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #000' }}>
                  <th style={{ padding: '8px', textAlign: 'left', borderRight: '1px solid #000', fontWeight: 'bold' }}>Customer</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderRight: '1px solid #000', fontWeight: 'bold' }}>Company</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderRight: '1px solid #000', fontWeight: 'bold' }}>Phone</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #000', fontWeight: 'bold' }}>Current</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #000', fontWeight: 'bold' }}>31-60 Days</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #000', fontWeight: 'bold' }}>61-90 Days</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #000', fontWeight: 'bold' }}>90+ Days</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row) => (
                  <tr
                    key={row.customer_id}
                    style={{ borderBottom: '1px solid #000' }}
                    className="no-print-hover"
                  >
                    <td style={{ padding: '6px', borderRight: '1px solid #000' }}>
                      {row.first_name} {row.last_name}
                    </td>
                    <td style={{ padding: '6px', borderRight: '1px solid #000' }}>{row.company || ''}</td>
                    <td style={{ padding: '6px', borderRight: '1px solid #000' }}>{row.phone}</td>
                    <td style={{ padding: '6px', textAlign: 'right', borderRight: '1px solid #000' }}>
                      {formatCurrency(row.current_total)}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', borderRight: '1px solid #000' }}>
                      {formatCurrency(row.days_31_60_total)}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', borderRight: '1px solid #000' }}>
                      {formatCurrency(row.days_61_90_total)}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', borderRight: '1px solid #000' }}>
                      {formatCurrency(row.days_90_plus_total)}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>
                      {formatCurrency(row.total_outstanding)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #000', fontWeight: 'bold' }}>
                  <td colSpan="3" style={{ padding: '8px', borderRight: '1px solid #000' }}>TOTAL</td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #000' }}>
                    {formatCurrency(totals.current)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #000' }}>
                    {formatCurrency(totals.days31_60)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #000' }}>
                    {formatCurrency(totals.days61_90)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #000' }}>
                    {formatCurrency(totals.days90Plus)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {formatCurrency(totals.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div>
              {reportData.map((customer) => {
                const invoices = getCustomerInvoices(customer.customer_id);
                return (
                  <div key={customer.customer_id} style={{ marginBottom: '30px', pageBreakInside: 'avoid' }}>
                    <h3 style={{
                      backgroundColor: '#f5f5f5',
                      padding: '10px',
                      borderBottom: '2px solid #333',
                      marginBottom: '0'
                    }}>
                      {customer.first_name} {customer.last_name}
                      {customer.company && ` - ${customer.company}`}
                      <span style={{ float: 'right' }}>
                        Total: {formatCurrency(customer.total_outstanding)}
                      </span>
                    </h3>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '13px',
                      marginBottom: '10px'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #ddd' }}>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Invoice #</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Invoice Date</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Due Date</th>
                          <th style={{ padding: '8px', textAlign: 'center' }}>Days Overdue</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Aging Bucket</th>
                          <th style={{ padding: '8px', textAlign: 'right' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => {
                          const daysOverdue = calculateDaysOverdue(invoice.due_date);
                          const bucket = getAgingBucket(invoice.due_date);
                          return (
                            <tr key={invoice.id} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '8px' }}>{invoice.invoice_number}</td>
                              <td style={{ padding: '8px' }}>{formatDate(invoice.invoice_date)}</td>
                              <td style={{ padding: '8px' }}>{formatDate(invoice.due_date)}</td>
                              <td style={{
                                padding: '8px',
                                textAlign: 'center',
                                color: daysOverdue > 0 ? '#d32f2f' : '#388e3c',
                                fontWeight: daysOverdue > 60 ? 'bold' : 'normal'
                              }}>
                                {daysOverdue > 0 ? daysOverdue : '-'}
                              </td>
                              <td style={{ padding: '8px' }}>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '3px',
                                  backgroundColor:
                                    bucket === 'Current' ? '#e8f5e9' :
                                    bucket === '31-60' ? '#fff3e0' :
                                    bucket === '61-90' ? '#ffe0b2' : '#ffcdd2',
                                  fontWeight: bucket === '90+' ? 'bold' : 'normal'
                                }}>
                                  {bucket}
                                </span>
                              </td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500' }}>
                                {formatCurrency(invoice.total_amount)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <style>{`
        @media screen {
          .print-only {
            display: none !important;
          }
          .no-print-hover:hover {
            background-color: #f5f5f5 !important;
            cursor: pointer;
          }
        }
        @media print {
          .no-print,
          header,
          nav,
          button,
          .bg-white.shadow-sm {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          body, html, * {
            background: white !important;
            background-color: white !important;
          }
          @page {
            margin: 0.5in;
            size: letter;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            margin: 0;
            padding: 0;
          }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          td, th { background: white !important; background-color: white !important; }
        }
      `}</style>
    </div>
  );
};

export default ARAgingReport;
